import { memo, useCallback } from 'react';
import { classnames } from '@/libs/utils';

const getSliderStyles = (trackHeight: string, thumbSize: string, scopeSelector?: string) => {
  const s = scopeSelector ? `.${scopeSelector} input[type="range"]` : `input[type="range"]`;
  return `
  ${s} {
    -webkit-appearance: none;
    width: 100%;
    height: ${trackHeight};
    border-radius: 0.5rem;
    background: transparent;
    outline: none;
  }

  ${s}:disabled {
    cursor: not-allowed;
  }

  ${s}::-webkit-slider-runnable-track {
    -webkit-appearance: none;
    height: ${trackHeight};
    border-radius: 0.5rem;
    background: linear-gradient(
      to right,
      #000 0%,
      #000 calc(((var(--value) - var(--min)) / (var(--max) - var(--min))) * 100%),
      #d1d5db calc(((var(--value) - var(--min)) / (var(--max) - var(--min))) * 100%),
      #d1d5db 100%
    );
  }

  ${s}:disabled::-webkit-slider-runnable-track {
    background: #e5e7eb;
  }

  ${s}::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: ${thumbSize};
    height: ${thumbSize};
    border-radius: 50%;
    background: white;
    cursor: pointer;
    border: 2px solid #000;
    margin-top: calc((${trackHeight} - ${thumbSize}) / 2);
  }

  ${s}:disabled::-webkit-slider-thumb {
    background: #e5e7eb;
    border-color: #9ca3af;
    cursor: not-allowed;
  }

  ${s}::-moz-range-track {
    height: ${trackHeight};
    border-radius: 0.5rem;
    background: linear-gradient(
      to right,
      #000 0%,
      #000 calc(((var(--value) - var(--min)) / (var(--max) - var(--min))) * 100%),
      #d1d5db calc(((var(--value) - var(--min)) / (var(--max) - var(--min))) * 100%),
      #d1d5db 100%
    );
    border: none;
  }

  ${s}:disabled::-moz-range-track {
    background: #e5e7eb;
  }

  ${s}::-moz-range-thumb {
    width: ${thumbSize};
    height: ${thumbSize};
    border-radius: 50%;
    background: white;
    cursor: pointer;
    border: 2px solid #000;
  }

  ${s}:disabled::-moz-range-thumb {
    background: #e5e7eb;
    border-color: #9ca3af;
    cursor: not-allowed;
  }
`;
};

interface RangeSliderProps {
  id: string;
  label?: string;
  min: number;
  max: number;
  step: number;
  value: number;
  onChange: (value: number) => void;
  showValue?: boolean;
  testId?: string;
  helperText?: string;
  disabled?: boolean;
  unit?: string;
  showValueContainerClassStyle?: string;
  trackHeight?: string;
  thumbSize?: string;
  scopeSelector?: string;
}

const RangeSlider = memo(
  ({
    id,
    label,
    min,
    max,
    step,
    value,
    onChange,
    showValue = false,
    testId,
    helperText,
    disabled = false,
    unit = 's',
    showValueContainerClassStyle = '',
    trackHeight = '0.5rem',
    thumbSize = '1.25rem',
    scopeSelector
  }: RangeSliderProps) => {
    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const numValue = parseFloat(e.target.value);
        if (!isNaN(numValue) && numValue >= min) {
          onChange(numValue);
        }
      },
      [onChange, min]
    );

    return (
      <>
        <style>{getSliderStyles(trackHeight, thumbSize, scopeSelector)}</style>
        <div className={scopeSelector ?? ''}>
          {label && (
            <label htmlFor={id} className="mb-2 block text-xs font-medium text-gray-700">
              {label}
            </label>
          )}
          <div
            className={classnames('flex items-center gap-3', showValue ? '' : 'w-full', disabled ? 'opacity-50' : '')}
          >
            <input
              id={id}
              data-testid={testId}
              type="range"
              min={min}
              max={max}
              step={step}
              value={value}
              onChange={handleChange}
              style={{ '--value': value, '--min': min, '--max': max } as React.CSSProperties}
              className="h-2 flex-1 cursor-pointer appearance-none disabled:cursor-not-allowed"
              disabled={disabled}
            />
            {showValue && (
              <div className={`w-8 rounded bg-gray-100 text-center ${showValueContainerClassStyle}`}>
                <span className="text-xs">{Math.round(value)}</span>
                <span className="ml-0.5 text-xs text-gray-400">{unit}</span>
              </div>
            )}
          </div>
          {helperText && <p className="mt-1 text-xs text-gray-500">{helperText}</p>}
        </div>
      </>
    );
  }
);

export default RangeSlider;
